import moment from "moment";
import { getEstimatedWorkMilliseconds } from "./presenceEstimatedTime";

const DAY = 86400000;
// La jornada del horario incluye 30 min de descanso: máximo acreditado por día.
const MAX_REST = 30 * 60 * 1000;
const durationColumn = (header, key) => ({ header, key, width: 22, style: { numFmt: "[h]:mm" } });
const signedDuration = (milliseconds) => {
  const minutes = Math.round(Math.abs(milliseconds) / 60000);
  return (milliseconds < 0 ? "-" : "+") + String(Math.floor(minutes / 60)).padStart(2, "0") + ":" + String(minutes % 60).padStart(2, "0");
};
const typeLabels = { entry: "Entrada", exit: "Salida", pause: "Pausa", resume: "Reanudación" };
const reasonLabels = {
  break: "Descanso", medical_appointment: "Cita médica", child_conciliation: "Conciliación hijos",
  family_conciliation: "Conciliación familiar", other: "Otra pausa",
};

// Recorrer páginas incluso si la API limita itemsPerPage a un tamaño menor.
export const fetchAllPresenceRecords = async (fetchPage, params) => {
  const records = [];
  const seen = new Set();
  for (let page = 1; ; page++) {
    const response = await fetchPage({ ...params, page, itemsPerPage: 50 });
    const data = response?.data;
    if (!Array.isArray(data?.["hydra:member"])) throw new Error("No se han podido obtener todos los fichajes");
    const members = data["hydra:member"];
    const total = data["hydra:totalItems"] == null ? null : Number(data["hydra:totalItems"]);
    if (!members.length) {
      if (total !== null && records.length < total) throw new Error("El informe está incompleto");
      break;
    }
    let added = 0;
    for (const record of members) {
      const key = record["@id"] || record.id || JSON.stringify(record);
      if (!seen.has(key)) {
        seen.add(key);
        records.push(record);
        added++;
      }
    }
    if (!added) throw new Error("La API no ha devuelto la siguiente página de fichajes");
    if (total !== null && records.length >= total) break;
    const view = data["hydra:view"];
    if (view && !view["hydra:next"] && total === null) break;
  }
  return records;
};

export const buildPresenceDetailReport = ({ records, employee, schedule, from, to, now }) => {
  const generatedAt = moment(now);
  const groups = new Map();
  const ordered = [...records].sort((a, b) => moment(a.recordedAt).valueOf() - moment(b.recordedAt).valueOf());
  ordered.forEach(record => {
    const date = moment(record.recordedAt).format("DD/MM/YYYY");
    if (!groups.has(date)) groups.set(date, []);
    groups.get(date).push(record);
  });
  const rows = [];
  for (const [date, signs] of groups) {
    let worked = 0;
    let paused = 0;
    let countedBreak = 0;
    let openWork = null;
    let openPause = null;
    let incomplete = false;
    const closePause = (at) => {
      const duration = Math.max(0, at.diff(moment(openPause.recordedAt)));
      paused += duration;
      if (openPause.pauseReason === "break") countedBreak += duration;
      openPause = null;
    };
    for (const sign of signs) {
      const at = moment(sign.recordedAt);
      if (sign.type === "entry" || sign.type === "resume") {
        if (openWork) incomplete = true;
        if (openPause) {
          closePause(at);
          if (sign.type !== "resume") incomplete = true;
        } else if (sign.type === "resume") incomplete = true;
        openWork = sign;
      } else if (sign.type === "pause" || sign.type === "exit") {
        if (openWork) worked += Math.max(0, at.diff(moment(openWork.recordedAt)));
        else incomplete = true;
        openWork = null;
        if (openPause) {
          closePause(at);
          incomplete = true;
        }
        if (sign.type === "pause") openPause = sign;
      }
    }
    const ongoing = Boolean(openWork || openPause);
    if (ongoing && moment(date, "DD/MM/YYYY").isSame(generatedAt, "day")) {
      if (openWork) worked += Math.max(0, generatedAt.diff(moment(openWork.recordedAt)));
      if (openPause) closePause(generatedAt);
    } else if (ongoing) incomplete = true;
    const creditedBreak = Math.min(countedBreak, MAX_REST);
    const credited = worked + creditedBreak;
    const estimated = getEstimatedWorkMilliseconds(date, schedule);
    rows.push({
      employee, date,
      entries: signs.filter(sign => sign.type === "entry" || sign.type === "resume").map(sign => moment(sign.recordedAt).format("HH:mm")).join(" / "),
      exits: signs.filter(sign => sign.type === "exit" || sign.type === "pause").map(sign => moment(sign.recordedAt).format("HH:mm")).join(" / "),
      paused: paused / DAY, extraRest: (countedBreak - creditedBreak) / DAY, worked: worked / DAY, estimated: estimated / DAY,
      difference: signedDuration(credited - estimated), credited: credited / DAY,
      status: incomplete ? "Fichajes incompletos" : ongoing ? "En curso" : "Completo",
    });
  }
  const totals = rows.reduce((sum, row) => ({
    worked: sum.worked + row.worked, estimated: sum.estimated + row.estimated, paused: sum.paused + row.paused, extraRest: sum.extraRest + row.extraRest,
    credited: sum.credited + row.credited,
  }), { worked: 0, estimated: 0, paused: 0, extraRest: 0, credited: 0 });
  const subtitle = employee + " · " + moment(from).format("DD/MM/YYYY") + " – " + moment(to).format("DD/MM/YYYY") +
    " · Generado: " + generatedAt.format("DD/MM/YYYY HH:mm") + ". Tiempos reales; la diferencia solo acredita 30 min de descanso al día; fichajes abiertos hasta esta hora.";
  return {
    filename: "presencia_" + moment(from).format("YYYY-MM-DD") + "_" + moment(to).format("YYYY-MM-DD") + ".xlsx",
    title: "Informe de Presencia",
    subtitle,
    sheets: [
      { name: "Resumen diario", columns: [
        { header: "Empleado", key: "employee", width: 32 }, { header: "Fecha", key: "date", width: 16 },
        { header: "Entradas / Reanudaciones", key: "entries", width: 30 }, { header: "Salidas / Pausas", key: "exits", width: 30 },
        durationColumn("Tiempo en pausa", "paused"), durationColumn("Tiempo trabajado", "worked"),
        durationColumn("Tiempo estimado", "estimated"), { header: "Diferencia", key: "difference", width: 18 },
        durationColumn("Descanso extra", "extraRest"),
        { header: "Estado", key: "status", width: 24 },
      ], rows: [...rows, { employee: "TOTAL", ...totals, difference: signedDuration((totals.credited - totals.estimated) * DAY) }] },
      { name: "Fichajes", columns: [
        { header: "Empleado", key: "employee", width: 32 }, { header: "Fecha", key: "date", width: 16 },
        { header: "Hora", key: "time", width: 16 }, { header: "Tipo", key: "type", width: 20 },
        { header: "Motivo de pausa", key: "reason", width: 26 }, { header: "Modalidad", key: "mode", width: 18 },
      ], rows: ordered.map(sign => ({
        employee, date: moment(sign.recordedAt).format("DD/MM/YYYY"), time: moment(sign.recordedAt).format("HH:mm:ss"),
        type: typeLabels[sign.type] || sign.type, reason: reasonLabels[sign.pauseReason] || sign.pauseReason || "",
        mode: sign.remote === true ? "Remoto" : sign.remote === false ? "Presencial" : "",
      })) },
    ],
  };
};

export const buildPresenceSummaryReport = ({ rows, from, to, filters, now }) => ({
  filename: "informe_presencia_" + moment(from).format("YYYY-MM-DD") + "_" + moment(to).format("YYYY-MM-DD") + ".xlsx",
  title: "Informe de Presencia",
  subtitle: moment(from).format("DD/MM/YYYY") + " – " + moment(to).format("DD/MM/YYYY") + " · " + filters +
    " · Generado: " + moment(now).format("DD/MM/YYYY HH:mm"),
  sheets: [{ name: "Resumen empleados", columns: [
    { header: "Empleado", key: "name", width: 36 }, { header: "Jornada de convenio", key: "hours", width: 22 },
    { header: "Tiempo total", key: "totalTime", width: 20 }, { header: "Tiempo efectivo", key: "workedTime", width: 20 },
    { header: "Parada descanso", key: "breakTime", width: 20 }, { header: "Parada conciliación", key: "otherPauseTime", width: 22 },
    { header: "Descanso extra", key: "extraBreakTime", width: 18 }, { header: "Diferencia", key: "difference", width: 18 }, { header: "Alertas", key: "alerts", width: 60 },
  ], rows: rows.map(row => ({
    name: String(row.name || ""), hours: String(row.hours ?? ""), totalTime: String(row.totalTime ?? ""),
    workedTime: String(row.workedTime ?? ""), breakTime: String(row.breakTime ?? ""),
    otherPauseTime: String(row.otherPauseTime ?? ""), extraBreakTime: String(row.extraBreakTime ?? ""), difference: String(row.difference ?? ""),
    alerts: (row.alerts || []).map(alert => alert.message).join(" | "),
  })) }],
});
